import classNames from 'classnames';

import Tooltip, { TooltipRows, TooltipTrigger } from '@/components/atoms/Tooltip';

import styles from './KpiTooltip.module.scss';

/** A terse line on a card, such as a bare ±, with what it describes one tooltip away (U11). */
export interface ExplainedLine {
    text: string;
    /** One fact per line, as the tooltip prints them. */
    explanation: string[];
}

export type KpiLine = string | ExplainedLine;

interface ExplainedTriggerProps {
    text: string;
    explanation: string[];
    figure: boolean;
}

function ExplainedTrigger(props: ExplainedTriggerProps) {
    const { text, explanation, figure } = props;
    return (
        <TooltipTrigger
            className={classNames(styles.explained, figure && styles.explainedFigure)}
            // Joined as sentences, so a screen reader pauses between the facts the tooltip
            // sets on separate lines.
            description={explanation.join('. ')}>
            {text}
        </TooltipTrigger>
    );
}

interface KpiLineTextProps {
    line: KpiLine;
    /**
     * Set when the line is a card's headline figure (U14). The trigger then sits inline,
     * so a unit after it stays on the figure's line, and its underline stays as thin as
     * under the small lines instead of growing with the figure.
     */
    figure?: boolean;
}

/** A card line as plain text, or as a trigger for its explanation when it has one. */
export function KpiLineText(props: KpiLineTextProps) {
    const { line, figure = false } = props;

    if (typeof line === 'string') return <>{line}</>;
    // The shared tooltip portals the bubble into the body, so the KPI grid neither clips it nor
    // grows around it.
    return (
        <Tooltip
            content={
                <TooltipRows>
                    {line.explanation.map((fact) => (
                        <div key={fact} className={styles.explanationFact}>
                            {fact}
                        </div>
                    ))}
                </TooltipRows>
            }
            placement="bottom"
            layout="fit"
            targetClassName={figure ? styles.figureAnchor : undefined}>
            <ExplainedTrigger text={line.text} explanation={line.explanation} figure={figure} />
        </Tooltip>
    );
}

/** The line as the card prints it, for keys. */
export function kpiLineKey(line: KpiLine): string {
    return typeof line === 'string' ? line : line.text;
}
